/**
 * ╔══════════════════════════════════════════════════════════════════════════════════════════════╗
 * ║                           EMAIL VERIFICATION REQUIRED SCREEN                                 ║
 * ║                        For users whose 7-day grace period has expired                        ║
 * ║                             CLOCK-IT STYLED AUTHENTICATION                                   ║
 * ║                                   FULL-SCREEN BLOCKER                                        ║
 * ╚══════════════════════════════════════════════════════════════════════════════════════════════╝
 */

import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { User } from '@supabase/supabase-js';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Alert, AlertDescription } from './ui/alert';
import { 
  Mail, 
  Clock, 
  AlertTriangle, 
  RefreshCw, 
  Shield,
  CheckCircle,
  LogOut,
  Timer
} from 'lucide-react';
import { 
  canResendVerification, 
  getResendCooldownRemaining, 
  markVerificationResent,
  formatGracePeriodExpiration
} from '../utils/gracePeriodCheck';

interface EmailVerificationRequiredProps {
  user: User;
  onResendEmail: () => Promise<void>;
  onSignOut: () => Promise<void>;
}

export function EmailVerificationRequired({ 
  user, 
  onResendEmail, 
  onSignOut 
}: EmailVerificationRequiredProps) {
  const [isResending, setIsResending] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [error, setError] = useState('');
  const [cooldownRemaining, setCooldownRemaining] = useState(0);

  // Update cooldown countdown timer
  useEffect(() => {
    const updateCooldown = () => {
      if (!user?.id) return;
      const remaining = getResendCooldownRemaining(user.id);
      setCooldownRemaining(remaining);
    };

    updateCooldown();
    const interval = setInterval(updateCooldown, 1000);
    return () => clearInterval(interval);
  }, [user?.id, isResending]);

  const handleResendEmail = async () => {
    if (!user?.id) return;

    // Check cooldown
    if (!canResendVerification(user.id)) {
      setError(`Please wait ${cooldownRemaining} seconds before resending`);
      return;
    }

    try {
      setIsResending(true);
      setError('');
      await onResendEmail();
      
      // Mark verification as resent (starts cooldown)
      markVerificationResent(user.id);
      setEmailSent(true);
      
      // Clear success message after 5 seconds
      setTimeout(() => setEmailSent(false), 5000);
    } catch (err: any) {
      setError(err.message || 'Failed to resend verification email');
    } finally {
      setIsResending(false);
    }
  };

  const handleSignOut = async () => {
    try {
      await onSignOut();
    } catch (err: any) {
      console.error('Sign out error:', err);
    }
  };

  const gracePeriodExpired = formatGracePeriodExpiration(user);
  const canResend = user?.id ? canResendVerification(user.id) : false;

  return (
    <div 
      className="fixed inset-0 bg-gradient-to-br from-blue-50 via-white to-indigo-50 flex items-center justify-center p-4"
      style={{ zIndex: 9999 }}
    >
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="w-full max-w-md"
      >
        {/* Header with Clock-It branding */}
        <div className="text-center mb-8">
          <motion.div
            initial={{ scale: 0.8 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.2, duration: 0.5 }}
            className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-blue-600 to-indigo-600 rounded-full mb-4 shadow-lg"
          >
            <Clock className="w-10 h-10 text-white" />
          </motion.div>
          <h1 className="text-3xl font-bold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent mb-2">
            Clock-It
          </h1>
          <p className="text-gray-600 text-sm">
            Worker Rights & Time Management Platform
          </p>
        </div>

        <Card className="shadow-xl border-0 bg-white/90 backdrop-blur-sm">
          <CardHeader className="text-center pb-4">
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.4, type: "spring", stiffness: 200 }}
              className="mx-auto mb-4 w-16 h-16 bg-red-100 rounded-full flex items-center justify-center"
            >
              <AlertTriangle className="w-8 h-8 text-red-600" />
            </motion.div>
            <CardTitle className="text-2xl font-bold text-gray-900 mb-2">
              Email Verification Required
            </CardTitle>
            <p className="text-gray-600 text-sm">
              {gracePeriodExpired ? `Grace period expired on ${gracePeriodExpired}` : 'Your trial period has expired'}
            </p>
          </CardHeader>

          <CardContent className="space-y-6">
            {/* User Context */}
            <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
              <div className="flex items-center mb-2">
                <Mail className="w-5 h-5 text-blue-600 mr-2" />
                <span className="font-medium text-blue-900">Your Account</span>
              </div>
              <p className="text-blue-800 text-sm break-all font-medium">
                {user.email}
              </p>
              <p className="text-blue-600 text-xs mt-1">
                Registered: {user.created_at ? new Date(user.created_at).toLocaleDateString() : 'Recently'}
              </p>
            </div>

            {/* Security-focused Explanation */}
            <div className="text-center">
              <p className="text-gray-700 mb-4">
                <strong>Secure your worker data</strong> by verifying your email address. 
                This ensures you receive important wage theft alerts, contract updates, and can recover your account if needed.
              </p>
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-left">
                <p className="text-amber-800 text-sm font-medium mb-1">
                  ⏰ Your 7-day grace period has ended
                </p>
                <p className="text-amber-700 text-xs">
                  Verify your email to continue protecting your worker rights and tracking your hours.
                </p>
              </div>
            </div>

            {/* Success/Error Messages */}
            {emailSent && (
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="bg-green-50 border border-green-200 rounded-lg p-4"
              >
                <div className="flex items-center">
                  <CheckCircle className="w-5 h-5 text-green-600 mr-2 flex-shrink-0" />
                  <span className="text-green-800 font-medium">Verification email sent!</span>
                </div>
                <p className="text-green-700 text-sm mt-1">
                  Check your inbox (and spam folder) for the confirmation link. Click the link to verify and regain access immediately.
                </p>
              </motion.div>
            )}

            {error && (
              <Alert className="border-red-200 bg-red-50">
                <AlertTriangle className="h-4 w-4 text-red-600" />
                <AlertDescription className="text-red-800 text-sm">
                  {error}
                </AlertDescription>
              </Alert>
            )}

            {/* Primary Action - Resend Email with Cooldown */}
            <div className="space-y-3">
              <Button
                onClick={handleResendEmail}
                disabled={isResending || !canResend}
                className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-semibold py-3 text-base shadow-lg hover:shadow-xl transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isResending ? (
                  <>
                    <RefreshCw className="w-5 h-5 mr-2 animate-spin" />
                    Sending Email...
                  </>
                ) : !canResend ? (
                  <>
                    <Timer className="w-5 h-5 mr-2" />
                    Wait {cooldownRemaining}s to Resend
                  </>
                ) : (
                  <>
                    <Mail className="w-5 h-5 mr-2" />
                    📧 Resend Verification Email
                  </>
                )}
              </Button>

              <div className="text-center text-sm text-gray-500">
                {canResend ? (
                  "Click above to get a new verification link"
                ) : (
                  "Cooldown prevents spam - wait a moment"
                )}
              </div>
            </div>

            {/* Secondary Actions */}
            <div className="pt-4 border-t border-gray-200">
              <Button
                variant="outline"
                onClick={handleSignOut}
                className="w-full text-gray-700 border-gray-300 hover:bg-gray-50"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Sign Out & Try Different Account
              </Button>
            </div>

            {/* Help Text - Security Focus */}
            <div className="bg-gray-50 rounded-lg p-4 text-center">
              <Shield className="w-5 h-5 text-gray-500 mx-auto mb-2" />
              <p className="text-xs text-gray-600">
                <strong>Worker Data Protection:</strong> Email verification helps prevent wage theft by ensuring 
                only you can access your time entries and worker rights information. We never share your data.
              </p>
            </div>

            {/* Troubleshooting Tips */}
            <details className="text-left">
              <summary className="text-sm text-blue-600 font-medium cursor-pointer hover:text-blue-700">
                Not receiving emails? Click for troubleshooting tips
              </summary>
              <div className="mt-3 space-y-2 text-xs text-gray-600 bg-blue-50 rounded-lg p-3">
                <p>✓ Check your spam/junk folder</p>
                <p>✓ Add noreply@clock-it.app to your contacts</p>
                <p>✓ Verify you entered the correct email address</p>
                <p>✓ Wait 2-3 minutes - emails can be delayed</p>
                <p>✓ Try signing out and back in if verification link doesn't work</p>
              </div>
            </details>
          </CardContent>
        </Card>

        {/* Footer */}
        <div className="text-center mt-6 text-xs text-gray-500">
          <p>Need help? Contact us at support@clock-it.app</p>
          <p className="mt-1 text-gray-400">Protecting worker rights since 2024</p>
        </div>
      </motion.div>
    </div>
  );
}